import { useCallback } from 'react';
import { useMutation } from '@ts-core/lib/apollo';

import { removeItemFromCache } from '@ts-core/core/utils/apolloUtils';

import {
  getAssignmentAnswersQuery,
  GetAssignmentAnswersQueryResult,
  GetAssignmentAnswersQueryVariables,
} from '../graphql/queries/getAssignmentAnswersQuery';
import { getAssignmentQuery } from '../graphql/queries/getAssignmentQuery';
import {
  removeAssignmentAnswerMutation,
  RemoveAssignmentAnswerMutationResult,
  RemoveAssignmentAnswerMutationVariables,
} from '../graphql/mutations/removeAssignmentAnswerMutation';

import { AssignmentAnswer } from '../types/index';

interface AssignmentAnswersActionsHookInput {
  courseId: string;
  assignmentId: string;
}

interface AssignmentAnswersActionsHookResult {
  onRemoveAssignmentAnswer: (answer: AssignmentAnswer) => void;
}

export default function useAssignmentAnswersActions(props: AssignmentAnswersActionsHookInput): AssignmentAnswersActionsHookResult {
  const { courseId, assignmentId } = props;

  /**
   * Remove assignment answer
   */
  const [removeAssignmentAnswer] = useMutation<RemoveAssignmentAnswerMutationResult, RemoveAssignmentAnswerMutationVariables>(
    removeAssignmentAnswerMutation,
    {
      refetchQueries: [{ query: getAssignmentQuery, variables: { courseId, assignmentId } }],
      update: removeItemFromCache<RemoveAssignmentAnswerMutationResult, GetAssignmentAnswersQueryResult, GetAssignmentAnswersQueryVariables>({
        query: getAssignmentAnswersQuery,
        queryName: 'answers',
        queryVariables: { courseId, assignmentId },
        mutationName: 'assignmentAnswer',
        idAccessor: 'id',
      }),
    }
  );
  const onRemoveAssignmentAnswer = useCallback(
    (answer: AssignmentAnswer) => {
      removeAssignmentAnswer({
        variables: {
          courseId,
          assignmentId,
          assignmentAnswerId: answer.id,
        },
        optimisticResponse: {
          assignmentAnswer: {
            _id: answer.id,
          },
        },
      });
    },
    [removeAssignmentAnswer, courseId, assignmentId]
  );

  return {
    onRemoveAssignmentAnswer,
  };
}
